// ============================================
// components/restaurant/RestaurantSignIn.js
// 3-Step Login for Restaurant:
//   Step 1 → Choose Business Type (Restaurant / Salon)
//   Step 2 → Enter Email
//   Step 3 → Enter Password / PIN
// ============================================

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Mail, Lock, Eye, EyeOff, KeyRound, ShieldCheck,
  ArrowLeft, ArrowRight, Loader2, Store, User, Utensils, Scissors,
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import axios from 'axios';
import Toast from '../../common/components/Toast';
import { RESTAURANT_API_URL, SALON_API_URL } from '../../api/config';
import RestaurantBg from '../../Pictures/restaurant.jpg';
import SalonBg from '../../Pictures/salon.jpg';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Derive API URL and dashboard route purely from chosen business type
const getApiForType  = (type) => type === 'salon' ? SALON_API_URL : RESTAURANT_API_URL;
const getDashForType = (type) => type === 'salon' ? '/salon/dashboard' : '/dashboard';

const METHOD_META = {
  password: {
    label: 'Password', desc: 'Use account password', color: 'blue',
    icon: Lock,
    border: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-blue-300',
    btn: 'bg-blue-600 hover:bg-blue-700',
    ring: 'focus:ring-blue-500/20 focus:border-blue-500',
    pinBorder: 'border-blue-300 focus:border-blue-500 focus:ring-blue-200',
    hint: null,
  },
  email_otp: {
    label: 'Email OTP', desc: 'Login using OTP sent to email', color: 'emerald',
    icon: Mail,
    border: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-emerald-300',
    btn: 'bg-emerald-600 hover:bg-emerald-700',
    ring: 'focus:ring-emerald-500/20 focus:border-emerald-500',
    pinBorder: 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-200',
    hint: '📧 Receive a 6-digit OTP via email to log in',
  },
  admin_pin: {
    label: 'Admin PIN', desc: '4-digit Admin PIN', color: 'indigo',
    icon: ShieldCheck,
    border: 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-indigo-300',
    btn: 'bg-indigo-600 hover:bg-indigo-700',
    ring: 'focus:ring-indigo-500/20 focus:border-indigo-500',
    pinBorder: 'border-indigo-300 focus:border-indigo-500 focus:ring-indigo-200',
    hint: '🔐 Restaurant Admin PIN',
  },
  waiter_pin: {
    label: 'Staff PIN', desc: '4-digit Staff PIN', color: 'amber',
    icon: User,
    border: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20',
    inactive: 'border-gray-200 dark:border-gray-700 hover:border-amber-300',
    btn: 'bg-amber-600 hover:bg-amber-700',
    ring: 'focus:ring-amber-500/20 focus:border-amber-500',
    pinBorder: 'border-amber-300 focus:border-amber-500 focus:ring-amber-200',
    hint: '📌 Staff PIN configured by Admin',
  },
};

function PinBoxes({ color, pinDigits, pinRefs, onChange, onKeyDown, loading }) {
  const meta = METHOD_META[color] || METHOD_META.admin_pin;
  return (
    <div className="flex justify-center gap-3">
      {pinDigits.map((digit, i) => (
        <input
          key={i}
          ref={el => pinRefs.current[i] = el}
          type="password"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={e => onChange(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          disabled={loading}
          className={`w-14 h-14 text-center text-2xl font-bold border-2 rounded-xl focus:outline-none focus:ring-2 transition-all
            ${meta.pinBorder} bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50`}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

function OtpBoxes({ otpDigits, otpRefs, onChange, onKeyDown, loading }) {
  return (
    <div className="flex justify-center gap-2">
      {otpDigits.map((digit, i) => (
        <input
          key={i}
          ref={el => otpRefs.current[i] = el}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={e => onChange(i, e.target.value)}
          onKeyDown={e => onKeyDown(i, e)}
          disabled={loading}
          className="w-11 h-12 text-center text-xl font-bold border-2 rounded-xl border-emerald-400 focus:border-emerald-600 focus:ring-2 focus:ring-emerald-200 bg-white dark:bg-gray-800 text-gray-900 dark:text-white disabled:opacity-50 transition-all focus:outline-none"
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}

export default function RestaurantSignIn() {
  const navigate = useNavigate();
  const { signIn: authSignIn } = useAuth();
  const pinRefs = useRef([]);
  const otpRefs = useRef([]);

  // ── Steps: 2 = email, 3 = auth method ──
  const [step, setStep]                   = useState(2);
  const [bizType, setBizType]             = useState('restaurant'); // 'restaurant' or 'salon'
  const [email, setEmail]                 = useState('');
  const [emailLoading, setEmailLoading]   = useState(false);
  const [methods, setMethods]             = useState({ password: true, emailOtp: true, adminPin: false, waiterPin: false });
  const [selectedMethod, setSelected]     = useState('password');
  const [password, setPassword]           = useState('');
  const [showPassword, setShowPass]       = useState(false);
  const [pinDigits, setPinDigits]         = useState(['', '', '', '']);
  const [otpDigits, setOtpDigits]         = useState(['', '', '', '', '', '']);
  const [otpSending, setOtpSending]       = useState(false);
  const [otpSent, setOtpSent]             = useState(false);
  const [otpTimer, setOtpTimer]           = useState(0);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [signInLoading, setSignInLoading] = useState(false);
  const [toast, setToast]                 = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => setToast({ show: true, message, type });
  const closeToast = () => setToast(t => ({ ...t, show: false }));

  // Persistent Timer for Resend OTP
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => setOtpTimer(t => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Focus PIN / OTP box when tab changes
  useEffect(() => {
    if (selectedMethod === 'email_otp' && otpSent && step === 3)
      setTimeout(() => otpRefs.current[0]?.focus(), 80);
    else if ((selectedMethod === 'admin_pin' || selectedMethod === 'waiter_pin') && step === 3)
      setTimeout(() => pinRefs.current[0]?.focus(), 80);
  }, [selectedMethod, otpSent, step]);

  // ── Step 1: Choose Business Type ──────────────────────────
  const handleChooseType = (type) => {
    if (type === 'salon') {
      navigate('/salon/signin');
      return;
    }
    setBizType(type);
    localStorage.setItem('businessType', type);
    setStep(2);
  };

  // ── Step 2 → Step 3: look up email on chosen backend ─────
  const handleEmailContinue = async () => {
    if (!EMAIL_RE.test(email.trim())) { showToast('Please enter a valid email address', 'error'); return; }
    setEmailLoading(true);
    try {
      const apiUrl = getApiForType(bizType);
      const res = await axios.post(`${apiUrl}/auth/check-login-methods`, {
        email: email.trim().toLowerCase(),
      });
      const m = res.data?.data?.availableMethods || { password: true, emailOtp: true };
      setMethods(m);
      if (m.password)       setSelected('password');
      else if (m.emailOtp)  setSelected('email_otp');
      else if (m.adminPin)  setSelected('admin_pin');
      else if (m.waiterPin) setSelected('waiter_pin');
      setStep(3);
    } catch (err) {
      showToast(err.response?.data?.message || `No ${bizType} account found for this email`, 'error');
    } finally { setEmailLoading(false); }
  };

  // ── Send Login OTP ────────────────────────────────────────
  const handleSendLoginOtp = async () => {
    if (!EMAIL_RE.test(email.trim())) { showToast('Please enter a valid email address', 'error'); return; }
    setOtpSending(true);
    try {
      const apiUrl = getApiForType(bizType);
      const res = await axios.post(`${apiUrl}/auth/send-login-otp`, {
        email: email.trim().toLowerCase(),
      });
      if (res.data.success) {
        setOtpSent(true);
        setOtpTimer(60);
        showToast('Login OTP has been sent to your email', 'success');
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to send OTP', 'error');
    } finally {
      setOtpSending(false);
    }
  };

  // ── Sign in ───────────────────────────────────────────────
  const handleSignIn = async (autoPin = null, autoOtp = null) => {
    let credential = '';
    if (selectedMethod === 'password') {
      credential = password;
      if (!credential) { showToast('Please enter your password', 'error'); return; }
      if (credential.length < 6) { showToast('Password must be at least 6 characters', 'error'); return; }
    } else if (selectedMethod === 'email_otp') {
      credential = autoOtp || otpDigits.join('');
      if (!credential) { showToast('Please enter the OTP', 'error'); return; }
      if (credential.length !== 6) { showToast('OTP must be 6 digits', 'error'); return; }
    } else {
      credential = autoPin || pinDigits.join('');
      if (!credential) { showToast('Please enter PIN', 'error'); return; }
      if (credential.length !== 4) { showToast('PIN must be 4 digits', 'error'); return; }
    }

    setSignInLoading(true);
    try {
      const apiUrl = getApiForType(bizType);
      const res = await axios.post(`${apiUrl}/auth/smart-login`, {
        email: email.trim().toLowerCase(),
        credential,
        credentialType: selectedMethod,
      });
      if (res.data.success) {
        const { token, user, isAdminSession } = res.data;
        await authSignIn({ token, user, isAdminSession: !!isAdminSession });
        showToast('Signed in successfully!');
        const dashRoute = getDashForType(bizType);
        navigate(dashRoute, { replace: true });
      }
    } catch (err) {
      showToast(err.response?.data?.message || 'Invalid credentials', 'error');
      if (selectedMethod === 'email_otp') {
        setOtpDigits(['', '', '', '', '', '']);
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      } else if (selectedMethod === 'admin_pin' || selectedMethod === 'waiter_pin') {
        setPinDigits(['', '', '', '']);
        setTimeout(() => pinRefs.current[0]?.focus(), 50);
      }
    } finally { setSignInLoading(false); }
  };

  const handleGoogleSignIn = () => {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID || '',
        callback: async (response) => {
          setGoogleLoading(true);
          try {
            const apiUrl = getApiForType(bizType);
            const res = await axios.post(`${apiUrl}/auth/google-login`, { idToken: response.credential });
            if (res.data.success) {
              await authSignIn({ token: res.data.token, user: res.data.user });
              showToast('Google Sign-In successful!');
              navigate(getDashForType(bizType), { replace: true });
            }
          } catch (err) {
            showToast(err.response?.data?.message || 'Google Sign-In failed', 'error');
          } finally { setGoogleLoading(false); }
        },
      });
      window.google.accounts.id.prompt();
    } else {
      showToast('Google Sign-In script not loaded. Refresh page.', 'error');
    }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...pinDigits]; next[index] = value.slice(-1); setPinDigits(next);
    if (value && index < 3) pinRefs.current[index + 1]?.focus();
    if (index === 3 && value && next.every(d => d)) handleSignIn(next.join(''));
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pinDigits[index] && index > 0) pinRefs.current[index - 1]?.focus();
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...otpDigits]; next[index] = value.slice(-1); setOtpDigits(next);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
    if (index === 5 && value && next.every(d => d)) handleSignIn(null, next.join(''));
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) otpRefs.current[index - 1]?.focus();
  };

  const availableTabs = [
    methods.password   && 'password',
    (methods.emailOtp ?? true) && 'email_otp',
    methods.adminPin   && 'admin_pin',
    methods.waiterPin  && 'waiter_pin',
  ].filter(Boolean);

  const meta = METHOD_META[selectedMethod] || METHOD_META.password;

  return (
    <div
      className="h-screen overflow-hidden flex flex-col"
      style={{
        backgroundImage: `url(${bizType === 'salon' ? SalonBg : RestaurantBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="absolute inset-0 bg-black/55 dark:bg-black/70 pointer-events-none z-0" />
      {toast.show && <Toast message={toast.message} type={toast.type} onClose={closeToast} />}

      {/* Header */}
      <header className="relative z-10 flex-shrink-0 flex items-center justify-between px-6 md:px-12 py-4 border-b border-white/10 bg-white/10 dark:bg-black/20 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <Utensils className="w-6 h-6 text-blue-400" />
          <h2 className="text-2xl font-bold text-white">TOBO RESTAURANT</h2>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/salon/signin')} className="px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white font-bold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5">
            <Scissors size={14} /> Salon Login
          </button>
        </div>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-4 overflow-hidden">
        <div className="w-full max-w-[440px] max-h-full overflow-y-auto bg-white/90 dark:bg-gray-900/90 backdrop-blur-xl rounded-3xl border border-white/30 dark:border-gray-700/50 p-8 md:p-10 shadow-2xl">

          {/* ══ STEP 1: Select Business Type ══ */}
          {step === 1 && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Store className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                </div>
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-2">Welcome Back</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Select your business type to get started</p>
              </div>

              <div className="space-y-4">
                {/* Restaurant Option */}
                <button
                  onClick={() => handleChooseType('restaurant')}
                  className="w-full group p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 bg-white dark:bg-gray-800 text-left transition-all hover:shadow-xl hover:shadow-blue-500/10 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Utensils size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-base">Restaurant / Cafe</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Billing, KOT, Tables &amp; Inventory</p>
                    </div>
                  </div>
                  <ArrowRight size={20} className="text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                </button>

                {/* Salon Option */}
                <button
                  onClick={() => handleChooseType('salon')}
                  className="w-full group p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-pink-500 dark:hover:border-pink-500 bg-white dark:bg-gray-800 text-left transition-all hover:shadow-xl hover:shadow-pink-500/10 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-pink-50 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Scissors size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 dark:text-white text-base">Salon / Spa</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Appointments, Services &amp; Staff</p>
                    </div>
                  </div>
                  <ArrowRight size={20} className="text-gray-400 group-hover:text-pink-600 group-hover:translate-x-1 transition-all" />
                </button>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800 text-center">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Don't have an account?{' '}
                  <button onClick={() => navigate('/signup')} className="font-bold text-blue-600 hover:underline">
                    Create Account
                  </button>
                </p>
              </div>
            </>
          )}

          {/* ══ STEP 2: Enter Email ══ */}
          {step === 2 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <button onClick={() => navigate('/')}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                  <Utensils size={12} /> Restaurant Login
                </div>
              </div>

              <div className="text-center mb-6">
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Enter Email</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Enter your registered restaurant email address</p>
              </div>

              <div className="space-y-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Email Address</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-600 dark:text-blue-400" />
                    <input
                      type="email" value={email} onChange={e => setEmail(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleEmailContinue()}
                      placeholder="restaurant@example.com" autoFocus autoCapitalize="none"
                      className="w-full h-12 rounded-xl pl-12 pr-4 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-600/20 focus:border-blue-600 transition-all text-sm"
                    />
                  </div>
                </div>

                <button onClick={handleEmailContinue}
                  disabled={emailLoading || !EMAIL_RE.test(email.trim())}
                  className="w-full h-12 rounded-2xl bg-blue-600 text-white font-bold text-sm shadow-lg shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {emailLoading
                    ? <><Loader2 className="animate-spin" size={18} /> Checking...</>
                    : <>Continue <ArrowRight size={16} /></>}
                </button>
              </div>
            </>
          )}

          {/* ══ STEP 3: Password / OTP / PIN ══ */}
          {step === 3 && (
            <>
              <div className="flex items-center justify-between mb-6">
                <button onClick={() => { setStep(2); setPassword(''); setPinDigits(['','','','']); setOtpDigits(['','','','','','']); setOtpSent(false); }}
                  className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-blue-600 transition-colors">
                  <ArrowLeft size={16} /> Back
                </button>
                <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 rounded-full border border-blue-200 dark:border-blue-800">
                  <Utensils size={12} className="text-blue-600 dark:text-blue-400" />
                  <span className="text-xs font-bold text-blue-700 dark:text-blue-300 truncate max-w-[170px]">{email}</span>
                </div>
              </div>

              <div className="text-center mb-6">
                <h1 className="text-3xl font-black text-gray-900 dark:text-white mb-1">Sign In</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Choose how you want to log in</p>
              </div>

              {availableTabs.length > 1 && (
                <div className={`grid gap-2 mb-5 ${availableTabs.length === 2 ? 'grid-cols-2' : availableTabs.length === 3 ? 'grid-cols-3' : 'grid-cols-4'}`}>
                  {availableTabs.map(method => {
                    const m = METHOD_META[method];
                    const MIcon = m.icon;
                    const active = selectedMethod === method;
                    return (
                      <button key={method}
                        onClick={() => { setSelected(method); setPinDigits(['','','','']); setOtpDigits(['','','','','','']); setPassword(''); }}
                        className={`flex flex-col items-center gap-1 p-2.5 rounded-xl border-2 transition-all ${active ? m.border : m.inactive}`}
                      >
                        <MIcon size={18} className={active ? `text-${m.color}-600` : 'text-gray-400'} />
                        <span className={`text-[11px] font-bold ${active ? `text-${m.color}-600` : 'text-gray-500'}`}>{m.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {meta.hint && (
                <p className={`text-center text-xs font-medium mb-4 text-${meta.color}-600 dark:text-${meta.color}-400`}>{meta.hint}</p>
              )}

              {selectedMethod === 'password' && (
                <div className="space-y-2 mb-5">
                  <div className="flex justify-between">
                    <label className="text-sm font-semibold text-gray-900 dark:text-gray-200">Password</label>
                    <button onClick={() => navigate('/restaurant/forgot-password')}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700">Forgot Password?</button>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-blue-500" />
                    <input
                      type={showPassword ? 'text' : 'password'} value={password}
                      onChange={e => setPassword(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSignIn()}
                      placeholder="Enter password" autoFocus
                      className="w-full h-12 rounded-xl pl-12 pr-12 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-sm"
                    />
                    <button type="button" onClick={() => setShowPass(v => !v)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-blue-600 transition-colors">
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
              )}

              {selectedMethod === 'email_otp' && (
                <div className="space-y-4 mb-5">
                  {!otpSent ? (
                    <div className="text-center space-y-4 py-2">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Click below to receive a 6-digit OTP code on <span className="font-semibold text-gray-800 dark:text-gray-200">{email}</span>
                      </p>
                      <button
                        onClick={handleSendLoginOtp}
                        disabled={otpSending}
                        className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                      >
                        {otpSending ? <><Loader2 className="animate-spin" size={18} /> Sending OTP...</> : 'Send OTP to Email'}
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <label className="block text-sm font-semibold text-gray-900 dark:text-gray-200 text-center">
                        Enter 6-digit OTP Code
                      </label>
                      <OtpBoxes otpDigits={otpDigits} otpRefs={otpRefs} onChange={handleOtpChange} onKeyDown={handleOtpKeyDown} loading={signInLoading} />
                      <div className="flex justify-between items-center text-xs px-1">
                        <span className="text-gray-500">Code sent to email</span>
                        <button
                          type="button"
                          disabled={otpTimer > 0 || otpSending}
                          onClick={handleSendLoginOtp}
                          className="font-semibold text-emerald-600 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {otpTimer > 0 ? `Resend Code (${otpTimer}s)` : 'Resend Code'}
                        </button>
                      </div>
                      <button
                        onClick={() => handleSignIn()}
                        disabled={signInLoading || otpDigits.some(d => !d)}
                        className="w-full h-12 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm shadow-lg shadow-emerald-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                      >
                        {signInLoading ? <><Loader2 className="animate-spin" size={18} /> Verifying...</> : 'Verify & Sign In'}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {(selectedMethod === 'admin_pin' || selectedMethod === 'waiter_pin') && (
                <div className="space-y-4 mb-5">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-gray-200 text-center">
                    Enter 4-digit {selectedMethod === 'admin_pin' ? 'Admin' : 'Staff'} PIN
                  </label>
                  <PinBoxes color={selectedMethod} pinDigits={pinDigits} pinRefs={pinRefs}
                    onChange={handlePinChange} onKeyDown={handlePinKeyDown} loading={signInLoading} />
                </div>
              )}

              {selectedMethod === 'password' && (
                <button onClick={() => handleSignIn()}
                  disabled={signInLoading || password.length < 6}
                  className="w-full h-12 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-lg shadow-blue-600/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                >
                  {signInLoading ? <><Loader2 className="animate-spin" size={18} /> Signing in...</> : 'Sign In'}
                </button>
              )}

              {(selectedMethod === 'admin_pin' || selectedMethod === 'waiter_pin') && signInLoading && (
                <div className="flex items-center justify-center gap-2 py-3 text-slate-500">
                  <Loader2 className="animate-spin" size={18} />
                  <span className="text-sm font-medium">Verifying PIN...</span>
                </div>
              )}

              {/* Google Sign In Option */}
              {selectedMethod === 'password' && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button onClick={handleGoogleSignIn} disabled={googleLoading}
                    className="w-full h-11 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 text-xs font-bold hover:bg-gray-50 transition-all flex items-center justify-center gap-2">
                    <svg className="w-4 h-4" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"/>
                    </svg>
                    Continue with Google
                  </button>
                </div>
              )}
            </>
          )}

        </div>
      </main>
    </div>
  );
}
